import { useWorkspaceText } from './interface-copy';
import { useContext, useRef } from 'react';
import { ArrowUpRight, RefreshCw, Sparkles } from 'lucide-react';
import { StarterSuggestionsContext } from '@/lib/starter-suggestions';
import { useWorkspace } from './context';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import { WorkspaceUploadInput } from './upload-input';
import { SetupNudge } from '@/company/onboarding/setup-nudge';
import { beginAgentCreation } from './agents/authoring';
import { isNewAppCanvas } from './apps/new-app';
import { makingNewAgent } from './agents/new-agent';
import { routeFor } from './navigation';
import { useNamespaceCopy } from '@/i18n/ns';
import homeSuggestionsCopy from '@/i18n/ns/home-suggestions';
import { projectHomeId } from './projects/home';

const PAUSE_KEY = {
  credits_exhausted: 'credits',
  plan_required: 'plan',
  member_limit_reached: 'limit',
  none: 'paused',
} as const;

export function WorkspaceStarterHeading() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const starters = useContext(StarterSuggestionsContext);
  const pauseCopy = useNamespaceCopy(homeSuggestionsCopy);
  const upload = useRef<HTMLInputElement>(null);
  if (
    isNewAppCanvas(ws.canvas) ||
    makingNewAgent(ws.turnContext) ||
    ws.canvas?.kind === 'storefront' ||
    projectHomeId(ws)
  )
    return null;
  const skill = ws.turnContext.intent.mode === 'skill-author';
  if (skill && ws.turnContext.intent.skillId) return null;
  const actions: {
    title: string;
    description: string;
    icon: WorkspaceIconName;
    run: () => void;
  }[] = [
    {
      title: uiText('Create an agent'),
      description: uiText('Give recurring work a helping hand'),
      icon: 'agents',
      run: () => beginAgentCreation(ws),
    },
    {
      title: uiText('Create a Skill'),
      description: uiText('Save a reusable way of working'),
      icon: 'skills',
      run: () => ws.authorSkill(),
    },
    {
      title: uiText('Take meeting notes'),
      description: uiText('Capture decisions and next steps'),
      icon: 'meetings',
      run: () => ws.navigate('meetings'),
    },
    {
      title: uiText('Work with a file'),
      description: uiText('Start from a document or image'),
      icon: 'upload',
      run: () => upload.current?.click(),
    },
    {
      title: uiText('Browse agents'),
      description: uiText('Open your agents and company agents'),
      icon: 'agents',
      run: () => ws.navigate('agents'),
    },
    {
      title: uiText('Review results'),
      description: uiText('Return to work saved in your Library'),
      icon: 'results',
      run: () => ws.navigate('results'),
    },
  ];
  return (
    <div className="ws-home-start">
      {!skill && (
        <>
          <SetupNudge />
          <div className="ws-home-shortcuts" aria-label={uiText('Start something')}>
            {actions.map((action) => (
              <button
                key={action.title}
                aria-label={action.title}
                onClick={action.run}
                disabled={action.icon === 'upload' && ws.busy}
              >
                <WorkspaceIcon name={action.icon} size={18} />
                <span className="ws-home-shortcut-copy">
                  <strong>{action.title}</strong>
                  <small>{action.description}</small>
                </span>
                <ArrowUpRight size={14} aria-hidden="true" />
              </button>
            ))}
          </div>
          <WorkspaceUploadInput
            ref={upload}
            label={uiText('Choose a file to work with')}
            disabled={ws.busy}
            onFiles={(files) => ws.upload(files, false, { attach: true })}
          />
        </>
      )}
      <>
        <div className="ws-home-section-heading">
          <div>
            <h2>
              <Sparkles size={16} aria-hidden="true" />{' '}
              {skill ? uiText('Skills worth adding') : uiText('Picked for you')}
            </h2>
            <p>
              {skill
                ? uiText('Reusable work grounded in your company knowledge.')
                : uiText('Ideas from your company context and recent work. Pick one to start.')}
            </p>
          </div>
          <button
            className="ws-home-refresh"
            onClick={starters.refresh}
            disabled={starters.loading}
            aria-label={uiText('Refresh suggested tasks')}
            title={
              skill
                ? uiText('Check for new company context.')
                : uiText('Check for new context. Ideas also refresh daily.')
            }
          >
            <RefreshCw size={14} aria-hidden="true" />
            <span>{uiText('Refresh')}</span>
          </button>
        </div>
        <div className="ws-home-status" role="status">
          {starters.pause ? (
            // Billing pauses new suggestions; the stored cards stay, and Usage explains and offers the plan action.
            <>
              {pauseCopy[PAUSE_KEY[starters.pause.detail ?? 'none']]}{' '}
              <a className="ws-home-status-link" href={routeFor('usage')}>
                {pauseCopy.seeUsage}
              </a>
            </>
          ) : starters.prompts.length && !skill ? null : starters.loading ? (
            uiText('Finding useful starting points…')
          ) : starters.prompts.length ? null : (
            uiText(
              'Suggestions aren’t available right now. Try Refresh, or describe the work you want to do.',
            )
          )}
        </div>
      </>
    </div>
  );
}
